import React, { useContext } from 'react'
import { useHistory, Link } from 'react-router-dom'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import IconButton from '@mui/material/IconButton'
import Tooltip from '@mui/material/Tooltip'

import ContentDossierToolbar from '@/feature/ContentDossier/Toolbar'
import { KeDoContext } from '../../../kedo-context'
import { useChat } from '../../../context/ChatContext'
import {
    BreadcrumbLink,
    BreadcrumbStatic,
    StyledBreadcrumbs,
} from '@/shared/ui/breadcrumbs'

type Props = {
    dossier: any
    defdossier: any
    showBreadcrumb?: boolean
    onDeleteSuccess?: () => void
    subscribed?: boolean
    favorite?: boolean
    items?: any
    content?: any
}

const DefaultHeader = ({
    dossier,
    defdossier,
    showBreadcrumb,
    onDeleteSuccess,
    subscribed,
    favorite,
    items,
    content,
}: Props) => {
    const kedo = useContext(KeDoContext)
    const { setIsChatOpen, setChatData } = useChat()

    const handleChatOpen = () => {
        setChatData({
            dossier,
            defdossier,
            items,
            content,
        })
        setIsChatOpen(true)
    }

    const history = useHistory()

    const defaultQuery = defdossier?.settings?.query_settings?.default_query
        ? `?dossierQuery=${defdossier?.settings?.query_settings?.default_query}`
        : ''

    const isChatEnabled = !!kedo?.env()?.getModule('artificialintelligence')?.id
    const canUseChat =
        kedo.isAdmin() ||
        ((kedo.isEnvironmentAdmin() || kedo.env().hasCredential('ai_chat')) &&
            isChatEnabled)

    return (
        <Box className="sticky dossier_info_header">
            <Box
                sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'flex-start',
                    justifyContent: 'space-between',
                    gap: 2,
                    width: '100%',
                }}
            >
                <Box
                    className="dossierInfo"
                    sx={{ flex: '1 1 200px', minWidth: 0 }}
                >
                    {showBreadcrumb !== false ? (
                        <Box sx={{ my: 1 }}>
                            <StyledBreadcrumbs
                                aria-label="breadcrumb"
                                sx={{ mb: 0 }}
                            >
                                <BreadcrumbLink component={Link} to={`/`}>
                                    {kedo.t('Home')}
                                </BreadcrumbLink>
                                <BreadcrumbLink
                                    component={Link}
                                    to={`/defdossier/${dossier.def_dossier_id}${defaultQuery}`}
                                >
                                    {kedo
                                        .env()
                                        .translateItem(
                                            defdossier,
                                            'defdossier',
                                            true
                                        )}
                                </BreadcrumbLink>
                                <BreadcrumbStatic className="current">
                                    {dossier?.summary}
                                </BreadcrumbStatic>
                            </StyledBreadcrumbs>
                        </Box>
                    ) : null}
                </Box>

                <Box
                    sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        justifyContent: 'flex-end',
                        flex: '1 1 50%',
                        maxWidth: '50%',
                        minWidth: 0,
                        '& > div': {
                            display: 'flex',
                            flexWrap: 'wrap',
                            justifyContent: 'flex-end',
                            width: '100%',
                            float: 'none',
                        },
                    }}
                >
                    <ContentDossierToolbar
                        onDeleteSuccess={onDeleteSuccess}
                        favorite={favorite}
                        subscribed={subscribed}
                        kedo={kedo}
                        id={dossier.id}
                        defdossier={defdossier}
                        dossier={dossier}
                    />
                </Box>
            </Box>

            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    width: '100%',
                    mt: 1,
                }}
            >
                <Typography
                    component="h1"
                    sx={{
                        fontSize: '2em',
                        fontWeight: 700,
                        lineHeight: 1.2,
                        mb: 0.5,
                    }}
                >
                    {dossier && dossier.summary ? dossier.summary : dossier?.id}
                </Typography>

                {canUseChat ? (
                    <Tooltip title="Chat">
                        <IconButton
                            size="small"
                            onClick={handleChatOpen}
                            aria-label="Open chat"
                        >
                            💬
                        </IconButton>
                    </Tooltip>
                ) : null}
            </Box>
        </Box>
    )
}

export default DefaultHeader
